import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Stack,
    Paper,
    Typography,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    FormLabel,
    FormControlLabel,
    MenuItem,
    RadioGroup,
    Checkbox,
    Radio,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAppDispatch } from "../../store/store";
import { createThirdPartyDeviceProvider } from "../../store/thunk/deviceIntegration";
import { LoadingButton } from "@mui/lab";
import { fetchDeviceProviderConfigurationList } from "../../store/thunk/thirdPartyDeviceThunk";

interface Field {
    name: string;
    type: string;
    label: string;
    required?: boolean;
    options?: string[];
}

interface Props {
    open: boolean;
    handleClose: () => void;
    tenant: any;
    providerData: any;
}

const CustomDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(2),
        minWidth: 450,
    },
}));

const formConfig: Record<string, Field[]> = {
    Verkada: [
        { type: "text", label: "API key", name: "api_key", required: true },
    ],
};

const providers = ["Verkada"];

// Mask function for API key
export function maskApiKey(apiKey: string): string {
    const totalLength = 15;
    const visiblePart = apiKey.slice(0, 4);
    const maskedPart = "*".repeat(totalLength - 4);
    return visiblePart + maskedPart;
}

export default function AddApiKeyDialog({ open, handleClose, tenant, providerData }: Props) {
    const [selectedProvider, setSelectedProvider] = useState<keyof typeof formConfig | null>(null);
    const [name, setName] = useState<string>("");
    const [saveLoader, setSaveLoader] = useState<boolean>(false);
    const [formData, setFormData] = useState<{ [key: string]: string | boolean }>({});
    const [errors, setErrors] = useState<any>({});
    // New state to hold the original API key
    const [originalApiKey, setOriginalApiKey] = useState<string>("");
    const dispatch = useAppDispatch();

    // Auto-fill the form when providerData exists
    useEffect(() => {
        if (providerData) {
            setName(providerData?.name || "");
            setSelectedProvider(providerData?.provider || null);
            try {
                const parsedData = providerData?.data ? JSON.parse(providerData.data) : {};
                // If there's an API key, store it and mask it for display
                if (parsedData.api_key) {
                    setOriginalApiKey(parsedData.api_key);
                    parsedData.api_key = maskApiKey(parsedData.api_key);
                }
                setFormData(parsedData);
            } catch (error) {
                setFormData({});
            }
        }
    }, [providerData]);

    useEffect(() => {
        // If providerId is stored in formData, update selectedProvider
        setSelectedProvider((formData?.providerId || null) as keyof typeof formConfig | null);
    }, [JSON.stringify(formData?.providerId)]);

    const handleCloseDialog = () => {
        setSelectedProvider(null);
        setName("");
        setFormData({});
        setErrors({});
        setOriginalApiKey("");
        handleClose();
    };

    const validateForm = () => {
        let newErrors: Record<string, string> = {};
        // Check that a provider is selected
        if (!selectedProvider) {
            newErrors["provider"] = "Provider is required";
        }

        // Check that a name is provided
        if (!name) {
            newErrors["name"] = "Name is required";
        }
        if (selectedProvider && formConfig[selectedProvider]) {
            formConfig[selectedProvider].forEach((field) => {
                if (field.required && !formData[field.name]) {
                    newErrors[field.name] = `${field.label} is required`;
                }
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
    ) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev: any) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
        setErrors((prev: any) => ({ ...prev, [name]: "" }));
    };

    const renderField = (field: Field) => {
        switch (field.type) {
            case "text":
                return (
                    <TextField
                        name={field.name}
                        value={formData?.[field.name] || ""}
                        label={field.label}
                        variant="outlined"
                        fullWidth
                        onChange={handleChange}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]}
                    />
                );
            case "select":
                return (
                    <FormControl fullWidth error={!!errors[field.name]}>
                        <InputLabel id={field.name}>{field.label}</InputLabel>
                        <Select
                            defaultValue={formData?.[field.name]}
                            value={formData?.[field.name] || ""}
                            labelId={field.name}
                            id={field.name}
                            label={field.label}
                            name={field.name}
                            onChange={handleChange}
                        >
                            {field.options?.map((option: any, idx: any) => (
                                <MenuItem key={idx} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography color="error" variant="caption">
                            {errors[field.name]}
                        </Typography>
                    </FormControl>
                );
            case "radio":
                return (
                    <FormControl error={!!errors[field.name]}>
                        <FormLabel>{field.label}</FormLabel>
                        <RadioGroup
                            name={field.name}
                            value={formData?.[field.name] || ""}
                            onChange={handleChange}
                            row
                        >
                            {field.options?.map((option: any, idx: any) => (
                                <FormControlLabel
                                    key={idx}
                                    value={option}
                                    control={<Radio />}
                                    label={option}
                                />
                            ))}
                        </RadioGroup>
                        <Typography color="error" variant="caption">
                            {errors[field.name]}
                        </Typography>
                    </FormControl>
                );
            case "checkbox":
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!formData?.[field.name]}
                                name={field.name}
                                onChange={handleChange}
                            />
                        }
                        label={field.label}
                    />
                );
            default:
                return null;
        }
    };

    const handleSubmit = () => {
        if (!validateForm()) return;
        // Remove keys that should not be part of dynamic data
        const { ...dynamicData } = removeKeys(formData, [
            "providerId",
            "pk",
            "sk",
            "created_at",
            "updated_at",
        ]);

        // If the API key hasn't been changed (i.e. it still shows the masked version), replace it with the original
        if (dynamicData.api_key && originalApiKey && dynamicData.api_key === maskApiKey(originalApiKey)) {
            dynamicData.api_key = originalApiKey;
        }

        const providerInfo = {
            provider: (selectedProvider || "") as string,
            data: JSON.stringify(dynamicData),
            name: (name || "") as string,
            tenantCode: tenant?.tenantCode,
            ...(providerData?.sk && { sk: providerData?.sk }),
            ...(providerData?.pk && { pk: providerData?.pk }),
        };

        setSaveLoader(true);
        dispatch(createThirdPartyDeviceProvider(providerInfo)).then(async (response) => {
            setSaveLoader(false);
            if (response?.type.includes("fulfilled")) {
                // Dispatch the fetch and wait for it to complete
                await dispatch(fetchDeviceProviderConfigurationList({ tenantCode: tenant?.tenantCode }));
                setFormData({});
                setSelectedProvider(null);
                handleCloseDialog();
            }
        });
    };

    return (
        <CustomDialog open={open} onClose={handleCloseDialog}>
            <DialogTitle sx={{ fontWeight: "bold", px: 1, marginBottom: 0, mt: 0.1, py: 0 }}>{providerData ? "Update Api Key" : "Add New Api Key"}</DialogTitle>
            <DialogContent sx={{ my: 0, pb: 2, px: 0.7 }}>
                <Stack spacing={2} marginTop={1}>
                    <Paper sx={{ width: "100%" }}>
                        <Autocomplete
                            options={providers}
                            value={selectedProvider}
                            renderInput={(params) => (
                                <TextField {...params}
                                    label="Select Provider"
                                    error={Boolean(errors.provider)}
                                    helperText={errors.provider}
                                    sx={{ mt: 2 }} />
                            )}
                            onChange={(_, newValue: string | null) =>
                                setSelectedProvider(newValue as keyof typeof formConfig | null)
                            }
                        />
                        {selectedProvider && (
                            <>
                                <TextField
                                    label="Name"
                                    variant="outlined"
                                    fullWidth
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    sx={{ mt: 2 }}
                                    error={Boolean(errors.name)}
                                    helperText={errors.name}
                                />
                                <Box component="div" sx={{ mt: 2 }}>
                                    {formConfig[selectedProvider]?.map((field: Field, index: any) => (
                                        <Box key={index} sx={{ mb: 2 }}>
                                            {renderField(field)}
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Paper>

                </Stack>
            </DialogContent>
            <DialogActions sx={{ pt: 2, borderTop: "1px solid #ccc" }}>
                <Button variant="outlined" color="secondary" onClick={handleCloseDialog}>
                    Cancel
                </Button>
                <LoadingButton
                    loading={saveLoader}
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    sx={{ mr: 1 }}
                >
                    {providerData ? "Update" : "Save"}
                </LoadingButton>
            </DialogActions>
        </CustomDialog>
    );
}

const removeKeys = (obj: Object, keysToRemove: string[]) => {
    return Object.fromEntries(Object.entries(obj).filter(([key]) => !keysToRemove.includes(key)));
};
